// PlanComponents/PlanVsActualCard.js
import React from 'react';
import { Typography, Table, TableBody, TableRow, TableCell, TableHead, Box, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useTheme } from '@mui/material/styles';
import { bg, text as textColors, border } from '../../../utils/darkModeAdapter';

import CardItem from './CardItem';
import { toManYenLabel } from './utils';

/**
 * 計画 vs 実績比較カード
 * 科学的根拠: SHRM Yield Ratio公式, マイナビ中途採用状況調査2025年版
 */
export default function PlanVsActualCard({ progressData }) {
  const theme = useTheme();

  if (!progressData) {
    return (
      <CardItem title="計画 vs 実績">
        <Typography variant="body2" sx={{ fontStyle: 'italic' }}>データがありません</Typography>
      </CardItem>
    );
  }

  const { actuals, plan_targets: targets, benchmarks } = progressData;

  const rows = [
    {
      label: '予算（総額）',
      plan: targets?.budget ? toManYenLabel(targets.budget) : '—',
      actual: actuals?.actual_budget_used !== null && actuals?.actual_budget_used !== undefined
        ? toManYenLabel(actuals.actual_budget_used) : '—',
      diff: targets?.budget && actuals?.actual_budget_used !== null && actuals?.actual_budget_used !== undefined
        ? toManYenLabel(actuals.actual_budget_used - targets.budget) : null,
    },
    {
      label: '採用人数',
      plan: targets?.target_hires ? `${targets.target_hires}名` : '—',
      actual: actuals?.current_hires !== null && actuals?.current_hires !== undefined
        ? `${actuals.current_hires}名` : '—',
      diff: targets?.target_hires && actuals?.current_hires !== null && actuals?.current_hires !== undefined
        ? `${actuals.current_hires - targets.target_hires}名` : null,
    },
    {
      label: '応募者数',
      plan: '—',
      actual: actuals?.current_applicants !== null && actuals?.current_applicants !== undefined
        ? `${actuals.current_applicants}名` : '—',
    },
    {
      label: '開始日',
      plan: targets?.start_date || '—',
      actual: actuals?.actual_start_date || '—',
    },
    {
      label: '終了日',
      plan: targets?.end_date || '—',
      actual: actuals?.actual_end_date || '—',
    },
  ];

  // 歩留まり率の計算
  const actualYieldRate = actuals?.actual_hires && actuals?.actual_applicants && actuals.actual_applicants > 0
    ? ((actuals.actual_hires / actuals.actual_applicants) * 100).toFixed(1)
    : null;

  // 実績CPH
  const actualCPH = actuals?.actual_hires && actuals.actual_hires > 0 && actuals?.actual_budget_used
    ? Math.floor(actuals.actual_budget_used / actuals.actual_hires)
    : null;

  return (
    <CardItem title="計画 vs 実績">
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

        {/* 比較テーブル */}
        <Table size="small" sx={{ '& th, & td': { py: 1 } }}>
          <TableHead>
            <TableRow sx={{ backgroundColor: bg(theme).subtle }}>
              <TableCell sx={{ fontWeight: 700, color: textColors(theme).secondary, border: 'none' }}>項目</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#4e7df3', border: 'none', textAlign: 'center' }}>計画</TableCell>
              <TableCell sx={{ fontWeight: 700, color: '#4caf50', border: 'none', textAlign: 'center' }}>実績</TableCell>
              <TableCell sx={{ fontWeight: 700, color: textColors(theme).muted, border: 'none', textAlign: 'center' }}>差分</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.label}>
                <TableCell sx={{ border: 'none', color: textColors(theme).secondary, fontWeight: 600 }}>
                  {row.label}
                </TableCell>
                <TableCell sx={{ border: 'none', textAlign: 'center', color: textColors(theme).primary }}>
                  {row.plan}
                </TableCell>
                <TableCell sx={{ border: 'none', textAlign: 'center', color: textColors(theme).primary }}>
                  {row.actual}
                </TableCell>
                <TableCell sx={{ border: 'none', textAlign: 'center', color: textColors(theme).muted }}>
                  {row.diff || '—'}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {/* 歩留まり率・CPH比較 */}
        <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(theme).secondary }}>
              KPI分析
            </Typography>
            <Tooltip title="歩留まり率 = 次段階通過者数 ÷ 前段階人数 × 100" arrow>
              <InfoOutlinedIcon sx={{ fontSize: 14, color: textColors(theme).muted, cursor: 'help' }} />
            </Tooltip>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            {/* 歩留まり率 */}
            <Box sx={{ flex: 1, minWidth: 140, p: 1.5, borderRadius: 2, textAlign: 'center', backgroundColor: bg(theme).card, border: `1px solid ${border(theme).light}` }}>
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>歩留まり率（応募→採用）</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: textColors(theme).primary, my: 0.5 }}>
                {actualYieldRate ? `${actualYieldRate}%` : '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                日本平均: {benchmarks?.japan_avg_yield_rate || 26.2}%
              </Typography>
            </Box>

            {/* 実績CPH */}
            <Box sx={{ flex: 1, minWidth: 140, p: 1.5, borderRadius: 2, textAlign: 'center', backgroundColor: bg(theme).card, border: `1px solid ${border(theme).light}` }}>
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>一人当たり採用コスト</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: textColors(theme).primary, my: 0.5 }}>
                {actualCPH ? toManYenLabel(actualCPH) : '—'}
              </Typography>
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                日本平均: {benchmarks?.japan_avg_cph_label || '28.9万円'}
              </Typography>
            </Box>

            {/* 内定辞退率 */}
            <Box sx={{ flex: 1, minWidth: 140, p: 1.5, borderRadius: 2, textAlign: 'center', backgroundColor: bg(theme).card, border: `1px solid ${border(theme).light}` }}>
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>内定辞退率（参考）</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: textColors(theme).primary, my: 0.5 }}>
                —
              </Typography>
              <Typography variant="caption" sx={{ color: textColors(theme).muted }}>
                日本平均: {benchmarks?.japan_offer_decline_rate || 9.3}%
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* 予算内訳比較 */}
        {(actuals?.actual_budget_media !== null || actuals?.actual_budget_agency !== null || actuals?.actual_budget_others !== null) && (
          <Box sx={{ p: 1.5, borderRadius: 2, backgroundColor: bg(theme).subtle, border: `1px solid ${border(theme).light}` }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: textColors(theme).secondary, mb: 1 }}>
              予算内訳（実績）
            </Typography>
            <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
              {actuals.actual_budget_media !== null && actuals.actual_budget_media !== undefined && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ff7043' }} />
                    <Typography variant="caption" sx={{ color: textColors(theme).muted }}>媒体費</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: textColors(theme).primary }}>
                    {toManYenLabel(actuals.actual_budget_media)}
                  </Typography>
                </Box>
              )}
              {actuals.actual_budget_agency !== null && actuals.actual_budget_agency !== undefined && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#ab47bc' }} />
                    <Typography variant="caption" sx={{ color: textColors(theme).muted }}>人材紹介費</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: textColors(theme).primary }}>
                    {toManYenLabel(actuals.actual_budget_agency)}
                  </Typography>
                </Box>
              )}
              {actuals.actual_budget_others !== null && actuals.actual_budget_others !== undefined && (
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Box sx={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#42a5f5' }} />
                    <Typography variant="caption" sx={{ color: textColors(theme).muted }}>その他</Typography>
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, color: textColors(theme).primary }}>
                    {toManYenLabel(actuals.actual_budget_others)}
                  </Typography>
                </Box>
              )}
            </Box>
          </Box>
        )}

        {/* 出典 */}
        <Typography variant="caption" sx={{ color: textColors(theme).muted, fontStyle: 'italic' }}>
          ※ ベンチマークは日本市場調査およびグローバル人事統計に基づく
        </Typography>
      </Box>
    </CardItem>
  );
}
